import { Box, Flex, Text } from '@chakra-ui/react';
import { AddButton } from '@themes/aegix';
import { SelectInput } from '@themes/aegix';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdDelete } from 'react-icons/md';

interface SiteItem {
  label: string;
  value: string;
}

interface UserSitesSectionProps {
  sites: SiteItem[];
  availableSites: SiteItem[];
  isAddMode: boolean;
  onAddModeChange: (isAdd: boolean) => void;
  onSiteSelect: (value: string) => void;
  onSiteDelete: (site: SiteItem) => void;
  canEdit?: boolean;
}

export const UserSitesSection = ({
  sites,
  availableSites,
  isAddMode,
  onAddModeChange,
  onSiteSelect,
  onSiteDelete,
  canEdit = true,
}: UserSitesSectionProps) => {
  const { t } = useTranslation();
  const sortedSites = [...sites].sort((a, b) => {
    if (!b.label) return -1;
    if (!a.label) return 1;
    return a.label.localeCompare(b.label);
  });

  return (
    <Box
      color={'text.primary'}
      bg={'surface.primary'}
      border={'2px solid'}
      borderColor={'border.secondary'}
      boxShadow={'0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)'}
      position={'relative'}
      borderRadius={'8px'}
      padding={'20px'}
    >
      <Flex
        justifyContent={'space-between'}
        alignItems={'center'}
        borderBottom={'1px solid'}
        borderColor={'border.secondary'}
        pb={3}
      >
        <Text fontSize={'20px'} fontWeight={600} textAlign={'start'}>
          {t('sites.title', t('common.sites', 'Sites'))}
        </Text>
        {canEdit && (
          <AddButton
            e2eTitle={'addSites'}
            onClick={() => {
              onAddModeChange(true);
              // speakAccessibility('add site');
            }}
          />
        )}
      </Flex>

      {isAddMode && (
        <Box py={2}>
          <SelectInput
            value=""
            option={availableSites}
            useMenuPortal={true}
            onChange={onSiteSelect}
            label={t('users.selectSiteToAdd', 'Select a site to add')}
            e2eTitle="user-site-add-select"
          />
        </Box>
      )}

      <Box pt={2}>
        {sortedSites.length > 0 ? (
          sortedSites.map((item, index) => (
            <Flex
              textAlign={'start'}
              alignItems={'center'}
              justifyContent={'space-between'}
              p={'5px'}
              key={index}
            >
              <Text color={'text.primary'}>{item.label}</Text>
              {canEdit && (
                <Box
                  cursor={'pointer'}
                  color={'surface.danger'}
                  role="button"
                  tabIndex={0}
                  aria-label={t('users.deleteSiteAria', 'Delete site {{name}}', { name: item.label })}
                  onClick={() => {
                    onSiteDelete(item);
                  }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSiteDelete(item);
                    }
                  }}
                >
                  <MdDelete />
                </Box>
              )}
            </Flex>
          ))
        ) : (
          <Text color={'text.secondary'} fontSize={'sm'}>
            {t('users.noSitesAssigned', 'No sites assigned')}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default UserSitesSection;
